/**
 * WP4a / #46607 — verification-shell contracts for Overview, Review queue,
 * Findings, and Runs.
 *
 * Session-authenticated product UI API (`apiCall` / `useApi`). Distinct from
 * the token-scoped public catalog at `/api/verification/v1`.
 *
 * Environment: every call carries `env` (query) and/or
 * `x-excellent-verification-environment`. Object ids are namespaced
 * `demo:` / `real:` (WP2). Handlers must call `enforceVerificationEnvironment`.
 *
 * HG11 (#46595): mutations use existing ROLE_CAPABILITIES — no new
 * capabilities. Review accept/reject requires `review:write` + `review:approve`.
 * Run create requires `api:write` (admin/owner; `api-client` is `api:read` only).
 * Reads are `*:read` per surface. Owner escape is `verification:*`.
 *
 * WP4b (workflows/controls/policies) and WP6 (UI pages) are out of scope.
 * Demo seed/reset is WP3 — this file does not own the demo provider.
 */

import { z } from "zod";
import { scoped, type Operation } from "../types";

export const VERIFICATION_SHELL_SCHEMA_VERSION = 1 as const;

const envName = z.enum(["demo", "real"]);
const verificationStatus = z.enum(["PASS", "MISMATCH", "FAIL", "UNKNOWN", "NOT_RUN", "PENDING"]);
const authority = z.enum(["deterministic", "semantic", "human", "imported"]);
const consequence = z.enum(["low", "medium", "high", "critical"]);
const findingStatus = z.enum(["open", "accepted_exception", "remediated", "dismissed"]);
const caseStatus = z.enum([
  "open",
  "assigned",
  "remediation_requested",
  "exception_requested",
  "exception_accepted",
  "escalated",
  "ready_for_reverification",
  "closed",
]);
const reviewDecision = z.enum([
  "accept_exception",
  "request_evidence",
  "approve_remediation",
  "reject_remediation",
  "dismiss",
]);
const runLifecycle = z.enum(["queued", "running", "completed", "failed", "blocked"]);
const namespacedId = z.string().min(1).max(200);
const iso = z.string().min(1);

export const verificationEnvQuery = z.object({
  env: envName.optional(),
  actorId: namespacedId.optional(),
});
export type VerificationEnvQuery = z.infer<typeof verificationEnvQuery>;

const provenance = z.object({
  environment: envName,
  demoScoped: z.boolean(),
  trustChain: envName,
});

export const verificationFindingDto = z.object({
  id: namespacedId,
  ref: z.string().min(1),
  runId: namespacedId,
  controlId: namespacedId,
  requirementId: namespacedId,
  verificationStatus: z.enum(["FAIL", "UNKNOWN"]),
  status: findingStatus,
  caseStatus,
  severity: consequence,
  ownerActorId: namespacedId.nullable(),
  assignedActorId: namespacedId.nullable(),
  openedAt: iso,
  dueAt: iso.nullable(),
  title: z.string().min(1),
  summary: z.string().min(1),
  ask: z.string().min(1),
  evidenceArtifactIds: z.array(namespacedId),
  citationRefs: z.array(z.string().min(1)),
  authority,
});
export type VerificationFindingDto = z.infer<typeof verificationFindingDto>;

export const verificationRunDto = z.object({
  id: namespacedId,
  label: z.string().min(1),
  lifecycle: runLifecycle,
  aggregate: verificationStatus,
  controlIds: z.array(namespacedId).min(1),
  startedAt: iso.nullable(),
  completedAt: iso.nullable(),
  createdByActorId: namespacedId,
  receiptId: namespacedId.nullable(),
  engineVersion: z.string().min(1),
  evidenceArtifactIds: z.array(namespacedId),
});
export type VerificationRunDto = z.infer<typeof verificationRunDto>;

export const verificationControlSummaryDto = z.object({
  id: namespacedId,
  ref: z.string().min(1),
  title: z.string().min(1),
  status: verificationStatus,
  consequence,
  openFindingCount: z.number().int().nonnegative(),
});
export type VerificationControlSummaryDto = z.infer<typeof verificationControlSummaryDto>;

export const verificationPackBlockerDto = z.object({
  controlId: namespacedId,
  ref: z.string().min(1),
  reason: z.string().min(1),
});
export type VerificationPackBlockerDto = z.infer<typeof verificationPackBlockerDto>;

const statusRollup = z.object({
  PASS: z.number().int().nonnegative(),
  MISMATCH: z.number().int().nonnegative(),
  FAIL: z.number().int().nonnegative(),
  UNKNOWN: z.number().int().nonnegative(),
  NOT_RUN: z.number().int().nonnegative(),
  PENDING: z.number().int().nonnegative(),
  total: z.number().int().nonnegative(),
});

export const verificationOverviewResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  tenant: z.object({
    id: namespacedId,
    name: z.string().min(1),
    framework: z.string().min(1),
    periodStart: z.string().min(1),
    periodEnd: z.string().min(1),
  }),
  ready: z.boolean(),
  controlCount: z.number().int().nonnegative(),
  blockingControlCount: z.number().int().nonnegative(),
  verifiedControlCount: z.number().int().nonnegative(),
  openFindingCount: z.number().int().nonnegative(),
  queueCount: z.number().int().nonnegative(),
  rollup: statusRollup,
  blockers: z.array(verificationPackBlockerDto),
  controls: z.array(verificationControlSummaryDto),
  recentRuns: z.array(verificationRunDto),
});
export type VerificationOverviewResponse = z.infer<typeof verificationOverviewResponse>;

export const verificationQueueResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  actorId: namespacedId.nullable(),
  items: z.array(verificationFindingDto),
});
export type VerificationQueueResponse = z.infer<typeof verificationQueueResponse>;

const findingsListQuery = verificationEnvQuery.extend({
  status: findingStatus.optional(),
  severity: consequence.optional(),
  controlId: namespacedId.optional(),
  assignedActorId: namespacedId.optional(),
});
export type VerificationFindingsListQuery = z.infer<typeof findingsListQuery>;

export const verificationFindingsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationFindingDto),
});
export type VerificationFindingsListResponse = z.infer<typeof verificationFindingsListResponse>;

const idParams = z.object({ id: namespacedId });
export type VerificationShellIdParams = z.infer<typeof idParams>;

export const verificationFindingGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  finding: verificationFindingDto,
});
export type VerificationFindingGetResponse = z.infer<typeof verificationFindingGetResponse>;

/**
 * The durable record of one review decision.
 *
 * It exists because the rationale used to be transmitted and dropped: the body
 * below has always carried `rationale`, and until #46341 the handler read only
 * `decision`, so a reviewer's stated reason reached the server and went nowhere.
 * A decision whose reason is not readable back is a decision with no
 * accountability, and the finding detail page told the reviewer otherwise.
 *
 * Append-only by construction: one record per decision, never an edit of the
 * finding row. `rationale` is carried VERBATIM — a normalized or truncated copy
 * would be a different claim than the one the reviewer signed.
 *
 * `draftedByWorker` is null when no worker drafted the text, which is the
 * honest reading whenever a human typed it. It is the denominator of the
 * rubber-stamping scorecard below, so attributing a human's own words to a
 * fictional drafter would report that reviewer as a rubber-stamper.
 */
export const verificationFindingDecisionDto = z.object({
  id: namespacedId,
  findingId: namespacedId,
  actorId: namespacedId,
  decision: reviewDecision,
  rationale: z.string().min(1),
  evidenceArtifactIds: z.array(namespacedId).min(1),
  policyId: namespacedId,
  decidedAt: iso,
  draftedByWorker: namespacedId.nullable(),
  draftEditDistance: z.number().int().nonnegative().nullable(),
});
export type VerificationFindingDecisionDto = z.infer<typeof verificationFindingDecisionDto>;

export const verificationFindingReviewBody = z.object({
  actorId: namespacedId,
  decision: reviewDecision,
  rationale: z.string().min(1),
  evidenceArtifactIds: z.array(namespacedId).min(1),
  policyId: namespacedId,
  /**
   * Draft provenance, when a worker really drafted the words. Optional because
   * most rationales are typed by the reviewer; absent means "not drafted", not
   * "drafted by nobody in particular".
   */
  draftedByWorker: namespacedId.nullable().optional(),
  draftEditDistance: z.number().int().nonnegative().nullable().optional(),
});
export type VerificationFindingReviewBody = z.infer<typeof verificationFindingReviewBody>;

export const verificationFindingReviewResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  finding: verificationFindingDto,
  decision: reviewDecision,
  decisionRecord: verificationFindingDecisionDto,
  capability: z.string().min(1),
});
export type VerificationFindingReviewResponse = z.infer<typeof verificationFindingReviewResponse>;

/**
 * `drafted` is the count of decisions a worker actually drafted; `edited` how
 * many of those the reviewer changed at all; `rate` the share. A `drafted` of
 * zero means there is nothing to measure — not that the reviewers are clean.
 * The reading surface must say which.
 */
export const verificationDraftScorecardDto = z.object({
  drafted: z.number().int().nonnegative(),
  edited: z.number().int().nonnegative(),
  rate: z.number().min(0).max(1),
});
export type VerificationDraftScorecardDto = z.infer<typeof verificationDraftScorecardDto>;

export const verificationFindingDecisionsResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationFindingDecisionDto),
  draftScorecard: verificationDraftScorecardDto,
});
export type VerificationFindingDecisionsResponse = z.infer<
  typeof verificationFindingDecisionsResponse
>;

const runsListQuery = verificationEnvQuery.extend({
  aggregate: verificationStatus.optional(),
  lifecycle: runLifecycle.optional(),
  controlId: namespacedId.optional(),
});
export type VerificationRunsListQuery = z.infer<typeof runsListQuery>;

export const verificationRunsListResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  items: z.array(verificationRunDto),
});
export type VerificationRunsListResponse = z.infer<typeof verificationRunsListResponse>;

export const verificationRunGetResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  run: verificationRunDto,
});
export type VerificationRunGetResponse = z.infer<typeof verificationRunGetResponse>;

export const verificationRunCreateBody = z.object({
  actorId: namespacedId,
  label: z.string().min(1),
  controlIds: z.array(namespacedId).min(1),
  engineVersion: z.string().min(1).optional(),
});
export type VerificationRunCreateBody = z.infer<typeof verificationRunCreateBody>;

export const verificationRunCreateResponse = z.object({
  schemaVersion: z.literal(VERIFICATION_SHELL_SCHEMA_VERSION),
  provenance,
  run: verificationRunDto,
  capability: z.string().min(1),
});
export type VerificationRunCreateResponse = z.infer<typeof verificationRunCreateResponse>;

const overviewGetOp: Operation<void, VerificationEnvQuery, void, VerificationOverviewResponse> = {
  method: "GET",
  path: () => "/api/verification/overview",
  query: verificationEnvQuery,
  response: verificationOverviewResponse,
};

const queueListOp: Operation<void, VerificationEnvQuery, void, VerificationQueueResponse> = {
  method: "GET",
  path: () => "/api/verification/queue",
  query: verificationEnvQuery,
  response: verificationQueueResponse,
};

const findingsListOp: Operation<
  void,
  VerificationFindingsListQuery,
  void,
  VerificationFindingsListResponse
> = {
  method: "GET",
  path: () => "/api/verification/findings",
  query: findingsListQuery,
  response: verificationFindingsListResponse,
};

const findingsGetOp: Operation<
  VerificationShellIdParams,
  VerificationEnvQuery,
  void,
  VerificationFindingGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/findings/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationEnvQuery,
  response: verificationFindingGetResponse,
};

const findingsDecisionsOp: Operation<
  VerificationShellIdParams,
  VerificationEnvQuery,
  void,
  VerificationFindingDecisionsResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/findings/${encodeURIComponent(id)}/decisions`,
  params: idParams,
  query: verificationEnvQuery,
  response: verificationFindingDecisionsResponse,
};

const findingsReviewOp: Operation<
  VerificationShellIdParams,
  VerificationEnvQuery,
  VerificationFindingReviewBody,
  VerificationFindingReviewResponse
> = {
  method: "POST",
  path: ({ id }) => `/api/verification/findings/${encodeURIComponent(id)}/review`,
  params: idParams,
  query: verificationEnvQuery,
  body: verificationFindingReviewBody,
  response: verificationFindingReviewResponse,
  // The decisions read is scoped to the finding just decided: recording a
  // rationale that the detail page then fails to show is the same defect as
  // never storing it.
  invalidates: [
    findingsListOp,
    queueListOp,
    overviewGetOp,
    scoped(findingsDecisionsOp, (args: { params: VerificationShellIdParams }) => ({
      id: args.params.id,
    })),
  ],
};

const runsListOp: Operation<void, VerificationRunsListQuery, void, VerificationRunsListResponse> = {
  method: "GET",
  path: () => "/api/verification/runs",
  query: runsListQuery,
  response: verificationRunsListResponse,
};

const runsGetOp: Operation<
  VerificationShellIdParams,
  VerificationEnvQuery,
  void,
  VerificationRunGetResponse
> = {
  method: "GET",
  path: ({ id }) => `/api/verification/runs/${encodeURIComponent(id)}`,
  params: idParams,
  query: verificationEnvQuery,
  response: verificationRunGetResponse,
};

const runsCreateOp: Operation<
  void,
  VerificationEnvQuery,
  VerificationRunCreateBody,
  VerificationRunCreateResponse
> = {
  method: "POST",
  path: () => "/api/verification/runs",
  query: verificationEnvQuery,
  body: verificationRunCreateBody,
  response: verificationRunCreateResponse,
  invalidates: [runsListOp, overviewGetOp],
};

export const verificationShell = {
  overview: overviewGetOp,
  queue: queueListOp,
  findingsList: findingsListOp,
  findingsGet: findingsGetOp,
  findingsDecisions: findingsDecisionsOp,
  findingsReview: findingsReviewOp,
  runsList: runsListOp,
  runsGet: runsGetOp,
  runsCreate: runsCreateOp,
};
